import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import React, { forwardRef, useMemo } from "react";
import DatePicker, {
  CalendarProps,
  DateObject,
  DatePickerProps,
  DatePickerRef,
  Value,
} from "react-multi-date-picker";
import { useAppSelector } from "redux/hooks";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import { COLORS } from "styles/colors";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import { ValidateWord } from "../text/Text";

export interface SingleDateProps
  extends Omit<CalendarProps, "onChange" | "value">,
    Omit<DatePickerProps, "onChange" | "value"> {
  allowTime?: boolean;
  onChange?: (value: DateObject | DateObject[] | null) => void;
  isValidated?: boolean;
  defaultHour?: number[];
  value?: Value | Value[];
  mode?: "single" | "range";
  "data-testid"?: string;
}
function SingleDate(props: SingleDateProps, ref: React.Ref<DatePickerRef>) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const {
    onChange,
    allowTime,
    isValidated,
    mode = "single",
    range = false,
    "data-testid": dataTestId = "atoms-calendar-single-date",
    ...rest
  } = props;

  const format = allowTime
    ? `${selectedAccount.dateFormat?.toUpperCase()} ${selectedAccount.timeFormat?.replace(
        " a",
        " A"
      )}`
    : selectedAccount.dateFormat?.toUpperCase();

  const plugins = useMemo(
    () =>
      allowTime
        ? [<TimePicker position="bottom" key="time-picker" format={format} />]
        : [],
    [allowTime]
  );
  return (
    <DatePickerWrapper
      isValidated={isValidated}
      disabled={props.disabled}
      className="date-input-wrapper"
      allowTime={allowTime}
      mode={mode}
    >
      <DatePicker
        ref={ref as React.RefObject<DatePickerRef>}
        format={format}
        onChange={onChange}
        dateSeparator=" - "
        range={false as unknown as boolean}
        plugins={plugins}
        showOtherDays
        arrow={<></>}
        {...rest}
        data-testid={dataTestId}
      >
        {isValidated && (
          <>
            <ValidateWord data-testid="atoms-calendar-validate">
              Please select a date
            </ValidateWord>
          </>
        )}
      </DatePicker>
    </DatePickerWrapper>
  );
}

const DatePickerWrapper = styled.div<{
  isValidated?: boolean;
  disabled?: boolean;
  allowTime?: boolean;
  mode?: "single" | "range";
}>`
  position: relative;
  .rmdp-container {
    width: 100%;
  }
  .validate-word {
    justify-content: center;
    padding: 0 0 10px;s
  }
  .rmdp-input {
    max-width: 100%;
    width: ${({ allowTime, mode }) =>
      mode === "range" ? (allowTime ? calcRem(165) : calcRem(80)) : "100%"};
    border: none;
    font-size: ${calcRem(14)};
    padding: 0 2px;
    height: 33px;
    background: none;
    color: ${({ isValidated }) =>
      isValidated ? COLORS.VALIDATERED : COLORS.BLACK};
    &:focus {
      outline: none;
      box-shadow: none;
    }
    &::placeholder {
      color: #abb5c5;
    }
    ${({ disabled, theme }) =>
      disabled &&
      `
        cursor: not-allowed;
        color: ${
          theme.value === "light"
            ? `${theme.COLORS.WHITE}60`
            : theme.COLORS.LIGHTGRAY
        };
      `}
  }
`;
export default forwardRef(SingleDate);
